<template>
  <div class="excel-viewer h-full">
    <div v-if="isLoading" class="flex items-center justify-center h-full">
      <div class="text-gray-500">Loading spreadsheet data...</div>
    </div>
    <div v-else class="h-full flex flex-col">
      <!-- Sheet selector tabs -->
      <div class="sheet-tabs flex overflow-x-auto mb-2 bg-gray-100 border-b border-gray-300">
        <button v-for="(sheet, index) in sheets" :key="index" :class="[
          'px-4 py-2 text-sm font-medium transition-colors',
          currentSheet === sheet
            ? 'bg-white border-l border-t border-r border-gray-300 text-blue-600 -mb-px'
            : 'text-gray-700 hover:bg-gray-200'
        ]" @click="selectSheet(sheet)">
          {{ sheet }}
        </button>
      </div>

      <!-- Spreadsheet container -->
      <div class="flex-grow relative">
        <div ref="hotContainer" class="w-full h-full"></div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, watch, onBeforeUnmount, onMounted } from 'vue';
import Handsontable from 'handsontable';
import * as XLSX from 'xlsx';
import { logger } from '@/services/logger';

// Import Handsontable styles
import 'handsontable/dist/handsontable.full.css';
import HyperFormula from 'hyperformula';

export default defineComponent({
  name: 'ExcelViewer',
  props: {
    fileData: {
      type: [ArrayBuffer, String, Object],
      default: null
    },
    sheetData: {
      type: Object,
      default: null
    },
    readOnly: {
      type: Boolean,
      default: true
    },
    cellCoordinates: {
      type: Array,
      default: () => []
    },
    select: {
      type: Array,
      default: () => [0,0]
    }
  },
  emits: ['cell-values'],
  setup(props, { expose, emit }) {
    const isLoading = ref(true);
    const hotInstance = ref<Handsontable | null>(null);
    const hotContainer = ref<HTMLElement | null>(null);
    const workbook = ref<XLSX.WorkBook | null>(null);
    const sheets = ref<string[]>([]);
    const currentSheet = ref('');
    const currentData = ref<any[][]>([]);

    // Process data for the current sheet
    const processSheetData = (sheetName: string) => {
      if (!workbook.value) return;

      try {
        // Get the worksheet
        const worksheet = workbook.value.Sheets[sheetName];

        // Convert to JSON with headers
        const json = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

        const cellValues: string[] = [];

        // Get the values of the specified cell coordinates
        if (props.cellCoordinates && props.cellCoordinates.length > 0) {
          (props.cellCoordinates as string[]).forEach((coordinate: string) => {
            const match = coordinate.match(/([A-Z]+)([0-9]+)/);
            if (match) {
              const [column, row] = match.slice(1);
              // Convert column letters to index (e.g. 'A' -> 0, 'AA' -> 26)
              const colIndex = column.split('').reduce((acc, char) =>
                acc * 26 + (char.charCodeAt(0) - 'A'.charCodeAt(0) + 1), 0) - 1;
              const rowIndex = parseInt(row) - 1;

              if (json.length > rowIndex && (json[rowIndex] as any[]).length > colIndex) {
                cellValues.push((json[rowIndex] as any[])[colIndex]);
              } else {
                cellValues.push(`Cell ${coordinate} is empty or out of range.`);
              }
            } else {
              cellValues.push(`Invalid cell coordinate: ${coordinate}`);
            }
          });
        }

        emit('cell-values', cellValues);

        if (json.length > 0) {
          currentData.value = json as any[][];

          // If Handsontable instance exists, update data
          if (hotInstance.value) {
            hotInstance.value.loadData(currentData.value);
            hotInstance.value.render();
          } else {
            // Initialize Handsontable if container exists
            initializeHandsontable();
          }
        } else {
          currentData.value = [];

          if (hotInstance.value) {
            hotInstance.value.loadData([]);
            hotInstance.value.render();
          }
        }
      } catch (error) {
        logger.error('Error processing sheet data:', { error, sheetName });
        currentData.value = [];
      }
    };

    // Initialize Handsontable instance
    const initializeHandsontable = () => {
      if (!hotContainer.value) return;

      if (hotInstance.value) {
        hotInstance.value.destroy();
      }

      hotInstance.value = new Handsontable(hotContainer.value, {
        data: currentData.value,
        colHeaders: true,
        rowHeaders: true,
        height: '100%',
        width: '100%',
        licenseKey: 'non-commercial-and-evaluation', // Free license for non-commercial use
        readOnly: props.readOnly,
        contextMenu: !props.readOnly,
        manualColumnResize: true,
        manualRowResize: true,
        stretchH: 'all',
        autoColumnSize: true,
        wordWrap: true,
        comments: true,
        fillHandle: !props.readOnly,
        formulas: {
          engine: HyperFormula
        },
        outsideClickDeselects: true,
        // Add themes for better appearance
        className: 'htMaterial',
        allowEmpty: true,
        dropdownMenu: !props.readOnly,
        filters: !props.readOnly
      });
    };

    // This function tries to select a cell, but sometimes there are strange errors happening
    const tryToSelectCell = (row: number, col: number) => {
      try {

        // This is the line which should be commented if strange errors happen
        hotInstance.value?.selectCell(row, col, row, col, true);
      
        
      } catch (error) {
        // Sometimes there are strange errors happening, therefore quick and dirty fix
        // If the application throws errors, just comment the line above
        console.error('Error selecting cell:', error);
      }
    };

    // Select a sheet to display
    const selectSheet = (sheetName: string) => {
      if (!workbook.value) return;

      currentSheet.value = sheetName;
      processSheetData(sheetName);
    };

    // Process Excel file
    const processFile = async () => {
      isLoading.value = true;

      try {
        if (!props.fileData) {
          logger.warn('No file data provided to ExcelViewer');
          return;
        }

        let data;
        if (props.fileData instanceof ArrayBuffer) {
          data = new Uint8Array(props.fileData);
        } else if (typeof props.fileData === 'string') {
          // Assuming it's a base64 string
          const binaryString = window.atob(props.fileData);
          data = new Uint8Array(binaryString.length);
          for (let i = 0; i < binaryString.length; i++) {
            data[i] = binaryString.charCodeAt(i);
          }
        } else {
          logger.warn('Unsupported file data format');
          return;
        }

        // Parse the file with full options
        workbook.value = XLSX.read(data, {
          type: 'array',
          cellDates: true,
          cellStyles: true,
          cellNF: true,
          cellFormula: true
        });

        // Get all sheet names
        sheets.value = workbook.value.SheetNames;

        // Default to first sheet
        if (sheets.value.length > 0) {
          selectSheet(sheets.value[0]);
        }

        logger.info('Excel file processed successfully');
      } catch (error) {
        logger.error('Error processing Excel file:', { error });
        sheets.value = [];
        currentData.value = [];
      } finally {
        isLoading.value = false;
      }
    };

    // Process JSON data (for when data is passed directly, not as a file)
    const processJsonData = () => {
      isLoading.value = true;

      try {
        if (!props.sheetData) {
          logger.warn('No sheet data provided to ExcelViewer');
          return;
        }

        // If sheetData is provided as an object like used in Kendo
        if (props.sheetData.sheets) {
          // Convert Kendo-style JSON to workbook format
          const wb = XLSX.utils.book_new();

          props.sheetData.sheets.forEach((sheet: any) => {
            // Create a worksheet from the rows
            const wsData: any[][] = [];

            sheet.rows.forEach((row: any) => {
              const rowData: any[] = [];
              row.cells.forEach((cell: any) => {
                if (cell.formula) {
                  // Handle formulas - store as formula strings
                  rowData.push(`=${cell.formula}`);
                } else {
                  rowData.push(cell.value);
                }
              });
              wsData.push(rowData);
            });

            const ws = XLSX.utils.aoa_to_sheet(wsData);
            XLSX.utils.book_append_sheet(wb, ws, sheet.name);
          });

          workbook.value = wb;
          sheets.value = wb.SheetNames;

          // Default to first sheet
          if (sheets.value.length > 0) {
            selectSheet(sheets.value[0]);
          }
        }

        logger.info('Sheet data processed successfully');
      } catch (error) {
        logger.error('Error processing sheet data:', { error });
        sheets.value = [];
        currentData.value = [];
      } finally {
        isLoading.value = false;
      }
    };

    // Process the file or data when props change
    watch(() => props.fileData, (newValue) => {
      if (newValue) {
        processFile();
      }
    }, { immediate: true });

    watch(() => props.sheetData, (newValue) => {
      if (newValue && !props.fileData) {
        processJsonData();
      }
    }, { immediate: true });

    // Watch for sheets array changes to automatically select first sheet
    watch(() => sheets.value, (newSheets) => {
      if (newSheets.length > 0 && !currentSheet.value) {
        selectSheet(newSheets[0]);
      }
    });

    // Watch for readOnly prop changes
    watch(() => props.readOnly, (newValue) => {
      if (hotInstance.value) {
        hotInstance.value.updateSettings({
          readOnly: newValue,
          contextMenu: !newValue,
          fillHandle: !newValue,
          dropdownMenu: !newValue,
          filters: !newValue
        });
      }
    });
    
    watch(() => props.select, (newSelect: any[]) => {
      console.log('newSelect', newSelect);

      if(newSelect.length == 2) {
        tryToSelectCell(newSelect[0], newSelect[1]);
      }
    });

    onMounted(() => {
      // Select first sheet on mount if available
      if (sheets.value.length > 0) {
        selectSheet(sheets.value[0]);
      }
    });

    // Destroy Handsontable instance when component is unmounted
    onBeforeUnmount(() => {
      if (hotInstance.value) {
        hotInstance.value.destroy();
        hotInstance.value = null;
      }
    });

    // Expose methods for parent components
    // Add this to the expose() function in the ExcelViewer component
    const getCurrentData = () => {
      // If we have a hot instance, get data directly from it
      if (hotInstance.value) {
        return {
          data: hotInstance.value.getData(),
          currentSheet: currentSheet.value
        };
      }

      // Otherwise, get data from the workbook
      if (workbook.value && currentSheet.value) {
        const worksheet = workbook.value.Sheets[currentSheet.value];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
        return {
          data: jsonData,
          currentSheet: currentSheet.value
        };
      }

      return null;
    };

    const getHotInstance = () => {
      return hotInstance.value;
    };

    const getWorkbook = () => {
      return workbook.value;
    };

    const exportToExcel = (filename: string = 'spreadsheet.xlsx') => {
      if (!workbook.value) return null;

      // Export the workbook to Excel file
      XLSX.writeFile(workbook.value, filename);
    };

    // Export to CSV
    const exportToCsv = (filename: string = 'spreadsheet.csv') => {
      if (!hotInstance.value) return null;

      // Get current data
      const data = hotInstance.value.getData();

      // Convert to CSV
      const ws = XLSX.utils.aoa_to_sheet(data);
      const csv = XLSX.utils.sheet_to_csv(ws);

      // Create a blob and download
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);

      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    };

    // Expose methods to parent components
    expose({
      getHotInstance,
      getWorkbook,
      exportToExcel,
      exportToCsv,
      getCurrentData
    });

    return {
      isLoading,
      sheets,
      currentSheet,
      hotContainer,
      selectSheet
    };
  }
});
</script>

<style scoped>
.excel-viewer {
  width: 100%;
  height: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

/* Custom styling for Handsontable */
:deep(.handsontable) {
  font-size: 0.9rem;
}

:deep(.ht_clone_top) {
  z-index: 11;
}

:deep(.ht_clone_left) {
  z-index: 10;
}

:deep(.ht_master) {
  z-index: 9;
}

:deep(.handsontable th) {
  background-color: #f3f4f6;
  color: #374151;
  font-weight: 600;
}

:deep(.handsontable td.current) {
  background-color: rgba(59, 130, 246, 0.15);
}

:deep(.handsontable .ht__active_highlight) {
  background-color: rgba(59, 130, 246, 0.2);
}

:deep(.ht_master .wtHolder::-webkit-scrollbar) {
  width: 8px;
  height: 8px;
}

:deep(.ht_master .wtHolder::-webkit-scrollbar-track) {
  background: #f1f1f1;
}

:deep(.ht_master .wtHolder::-webkit-scrollbar-thumb) {
  background: #c1c1c1;
  border-radius: 4px;
}

:deep(.ht_master .wtHolder::-webkit-scrollbar-thumb:hover) {
  background: #a1a1a1;
}
</style>
